import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";

import { LoginPage } from "@/features/auth";
import {
  ClientsPage,
  ClientDetailPage,
  ActionPersonPage,
  ActionCompanyPage,
} from "@/features/clients";
import {
  ContractsPage,
  ContractDetailPage,
  ActionContractPage,
} from "@/features/contracts";
import {
  MattersPage,
  MatterDetailPage,
  ActionMatterPage,
} from "@/features/matters";
// import { TasksPage, TaskDetailPage } from "@/features/tasks";
import { NewTasksPage, NewTaskDetailPage, MyTasksPage, LeadReviewPage, PartnerReviewPage, ApproveToBillingPage } from "@/features/new-tasks";
import {
  SettingsPage,
  ActivityTypesPage,
  TaskTypesPage,
  PaymentMethodsPage,
  ExpenseCategoriesPage,
  MatterPracticeAreasPage,
  ContractPracticeAreasPage,
  ContractPeriodsPage,
  QaCriteriaPage,
} from "@/features/settings";
import { CalendarPage } from "@/features/calendar";
import {
  BillsPage,
  BillDetailPage,
  PaymentsPage,
  PaymentDetailPage,
} from "@/features/billing";
import { UsersPage, UserDetailPage } from "@/features/users";
import { RolesPage, RoleDetailPage } from "@/features/roles";
import { AttendancesPage, AttendanceDetailPage } from "@/features/attendances";
import { DevicesPage, DeviceDetailPage } from "@/features/devices";
import { ReviewsPage, ReviewDetailPage } from "@/features/reviews";
import { ComplaintsPage, ComplaintDetailPage } from "@/features/complaints";
import { KPIsPage, KPIDetailPage } from "@/features/kpi";
import {
  HourlyPackagesPage,
  HourlyPackageDetailPage,
  SuccessPackagesPage,
  SuccessPackageDetailPage,
} from "@/features/packages";
import { ReportsPage, ReportClientFormatPage } from "@/features/reports";
import { LeoPage } from "@/features/leo";
import { ExpensesPage } from "@/features/expenses";
import { ROUTES } from "@/lib/constants/routes";
import { useDocumentTitle } from "@/lib/hooks";

import { AuthGuard, PermissionGuard, RoleTypeGuard } from "./guards";
import { MainLayout } from "./layouts";
import { DashboardPage, NotFoundPage, ForbiddenPage } from "./pages";

// Pathless root — wraps every route so the browser tab title tracks the
// current page in the active language.
function RootLayout() {
  useDocumentTitle();

  return <Outlet />;
}

// Create router with data router API
const appRoutes = [
    // Public routes
    {
      path: ROUTES.LOGIN,
      element: <LoginPage />,
    },

    // Report client-format (no sidebar / navbar)
    {
      path: `${ROUTES.APP}/reports/client-format/:contractId`,
      element: (
        <AuthGuard>
          <ReportClientFormatPage />
        </AuthGuard>
      ),
    },

    // Protected routes with layout
    {
      path: ROUTES.APP,
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        // Default redirect to dashboard
        { index: true, element: <Navigate to="dashboard" replace /> },

        // Dashboard
        { path: "dashboard", element: <DashboardPage /> },

        // Clients
        { path: "clients", element: <ClientsPage /> },
        { path: "clients/:id", element: <ClientDetailPage /> },
        { path: "clients/actionPerson", element: <PermissionGuard resource="client" action="create"><ActionPersonPage /></PermissionGuard> },
        { path: "clients/actionCompany", element: <PermissionGuard resource="client" action="create"><ActionCompanyPage /></PermissionGuard> },
        { path: "clients/actionPERSON/:id", element: <PermissionGuard resource="client" action="update"><ActionPersonPage /></PermissionGuard> },
        { path: "clients/actionCOMPANY/:id", element: <PermissionGuard resource="client" action="update"><ActionCompanyPage /></PermissionGuard> },

        // Contracts
        { path: "contracts", element: <ContractsPage /> },
        { path: "contracts/:id", element: <ContractDetailPage /> },
        { path: "contracts/action", element: <PermissionGuard resource="contract" action="create"><ActionContractPage /></PermissionGuard> },
        { path: "contracts/action/:id", element: <PermissionGuard resource="contract" action="update"><ActionContractPage /></PermissionGuard> },

        // Matters
        { path: "matters", element: <MattersPage /> },
        { path: "matters/new", element: <ActionMatterPage /> },
        { path: "matters/:id", element: <MatterDetailPage /> },
        { path: "matters/:id/edit", element: <ActionMatterPage /> },

        // Tasks
        // { path: "tasks", element: <TasksPage /> },
        // { path: "tasks/:id", element: <TaskDetailPage /> },

        // New Tasks
        { path: "new-tasks", element: <NewTasksPage /> },
        { path: "new-tasks/:id", element: <NewTaskDetailPage /> },
        { path: "my-tasks", element: <MyTasksPage /> },
        { path: "lead-review", element: <RoleTypeGuard roleType="LEAD"><LeadReviewPage /></RoleTypeGuard> },
        { path: "partner-review", element: <RoleTypeGuard roleType="PARTNER"><PartnerReviewPage /></RoleTypeGuard> },
        { path: "approve-to-billing", element: <RoleTypeGuard roleType="MD"><ApproveToBillingPage /></RoleTypeGuard> },

        // Settings
        { path: "settings", element: <SettingsPage /> },
        { path: "settings/activity-types", element: <PermissionGuard resource="activity-type" action="get"><ActivityTypesPage /></PermissionGuard> },
        { path: "settings/task-types", element: <PermissionGuard resource="task-type" action="get"><TaskTypesPage /></PermissionGuard> },
        { path: "settings/payment-methods", element: <PermissionGuard resource="payment-method" action="get"><PaymentMethodsPage /></PermissionGuard> },
        { path: "settings/expense-categories", element: <PermissionGuard resource="expense-category" action="get"><ExpenseCategoriesPage /></PermissionGuard> },
        { path: "settings/matter-practice-areas", element: <PermissionGuard resource="matter-practice-area" action="get"><MatterPracticeAreasPage /></PermissionGuard> },
        { path: "settings/contract-practice-areas", element: <PermissionGuard resource="contract-practice-area" action="get"><ContractPracticeAreasPage /></PermissionGuard> },
        { path: "settings/contract-periods", element: <PermissionGuard resource="contract-period" action="get"><ContractPeriodsPage /></PermissionGuard> },
        { path: "settings/qa-criteria", element: <PermissionGuard resource="qa-criteria" action="get"><QaCriteriaPage /></PermissionGuard> },


        // Calendar
        { path: "calendar", element: <CalendarPage /> },

        // Case 360° (Stage 8) — LEO Reference directory
        { path: "leo", element: <PermissionGuard resource="leo" action="get"><LeoPage /></PermissionGuard> },

        // Expenses
        { path: "expenses", element: <PermissionGuard resource="expense" action="get"><ExpensesPage /></PermissionGuard> },

        // Bills
        { path: "bills", element: <PermissionGuard resource="bill" action="get"><BillsPage /></PermissionGuard> },
        { path: "bills/:id", element: <PermissionGuard resource="bill" action="get"><BillDetailPage /></PermissionGuard> },

        // Payments
        { path: "payments", element: <PaymentsPage /> },
        { path: "payments/:id", element: <PaymentDetailPage /> },

        // Hourly Packages
        { path: "hourly-packages", element: <HourlyPackagesPage /> },
        { path: "hourly-packages/:id", element: <HourlyPackageDetailPage /> },

        // Success Packages
        { path: "success-packages", element: <SuccessPackagesPage /> },
        { path: "success-packages/:id", element: <SuccessPackageDetailPage /> },

        // KPI
        { path: "kpi", element: <KPIsPage /> },
        { path: "kpi/:id", element: <KPIDetailPage /> },

        // Users
        { path: "users", element: <UsersPage /> },
        { path: "users/:id", element: <UserDetailPage /> },

        // Roles
        { path: "roles", element: <RolesPage /> },
        { path: "roles/:id", element: <RoleDetailPage /> },

        // Attendances
        { path: "attendances", element: <AttendancesPage /> },
        { path: "attendances/:id", element: <AttendanceDetailPage /> },

        // Devices
        { path: "devices", element: <DevicesPage /> },
        { path: "devices/:id", element: <DeviceDetailPage /> },

        // Reviews
        { path: "reviews", element: <ReviewsPage /> },
        { path: "reviews/:id", element: <ReviewDetailPage /> },

        // Complaints
        { path: "complaints", element: <ComplaintsPage /> },
        { path: "complaints/:id", element: <ComplaintDetailPage /> },

        // Reports
        { path: "reports", element: <ReportsPage /> },

        // Error pages (inside layout)
        { path: "forbidden", element: <ForbiddenPage /> },
        { path: "not-found", element: <NotFoundPage /> },

        // Catch-all for unknown routes within /app
        { path: "*", element: <NotFoundPage /> },
      ],
    },

    // Redirect root-level error routes to layout versions
    { path: ROUTES.FORBIDDEN, element: <Navigate to="/app/forbidden" replace /> },
    { path: ROUTES.NOT_FOUND, element: <Navigate to="/app/not-found" replace /> },
    { path: "*", element: <Navigate to="/app/not-found" replace /> },
];

const router = createBrowserRouter(
  [{ element: <RootLayout />, children: appRoutes }],
  // {
  //   future: {
  //     v7_startTransition: true,
  //   },
  // },
);

export function AppRouter() {
  
  if (typeof window === "undefined") {
    return null;
  }

  return (
    // <Suspense fallback={<LoadingFallback />}>
      <RouterProvider router={router} />
    // </Suspense>
  );
}
